/**
 * Session Management Utilities
 *
 * Handles session expiration detection and management
 * Requirements: 13.2
 */

import type { Session } from '@supabase/supabase-js';

/**
 * Check if a session is expired or about to expire
 * @param session - The Supabase session to check
 * @param bufferSeconds - Number of seconds before expiration to consider expired (default: 60)
 * @returns true if session is expired or about to expire
 */
export const isSessionExpired = (session: Session | null, bufferSeconds: number = 60): boolean => {
  if (!session) {
    return true;
  }

  const expiresAt = session.expires_at;
  if (!expiresAt) {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresIn = expiresAt - now;

  return expiresIn <= bufferSeconds;
};

/**
 * Get the time remaining until session expiration
 * @param session - The Supabase session to check
 * @returns seconds until expiration, or null if no expiration time
 */
export const getSessionTimeRemaining = (session: Session | null): number | null => {
  if (!session?.expires_at) {
    return null;
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresIn = session.expires_at - now;

  return Math.max(0, expiresIn);
};

/**
 * Clear all authentication data from storage
 * Requirements: 13.2
 */
export const clearAuthData = (): void => {
  // Clear any stored tokens or user data
  localStorage.removeItem('supabase.auth.token');
  sessionStorage.clear();
};
